import type { ColDef } from 'ag-grid-community';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useResourceNavigation } from '@/hooks/useResourceNavigation';
import { useConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { ListPageLayout } from '@/components/layout/list-page-layout';
import { Badge } from '@/components/ui/badge';
import { DEFAULT_PAGE_SIZE } from '@/lib/constants';
import { getMutationOptions, showMutationSuccessToast } from '@/lib/mutationRefetch';
import { openEntityInNewWindow } from '@/lib/open-in-new-window';
import { TariffDetails, TariffDetailsHeader } from './tariff-details';
import { TARIFFS_LIST_QUERY, SET_MAIN_TARIFF_MUTATION } from './tariffs.gql.tsx';
import {
	selectTariffAfterSave,
	type TariffListNode
} from './tariff-list-selection';

interface TariffsListResponse {
	tariffs?: {
		nodes?: TariffListNode[] | null;
		pageInfo?: {
			hasNextPage: boolean;
			endCursor?: string | null;
		} | null;
	} | null;
}

const getTariffStatusLabel = (status: number): string => {
	switch (status) {
		case 0:
			return 'Draft';
		case 1:
			return 'Active';
		case 2:
			return 'Inactive';
		default:
			return 'Unknown';
	}
};

export const TariffsList = () => {
	const { t } = useTranslation('tariffs');
	const { goToCreate, goToEdit } = useResourceNavigation('tariffs');
	const apolloClient = useApolloClient();
	const location = useLocation();
	const navigate = useNavigate();

	const { showDialog } = useConfirmationDialog();
	const [setMainTariff] = useMutation(SET_MAIN_TARIFF_MUTATION);

	const [selectedTariff, setSelectedTariff] =
		useState<TariffListNode | null>(null);
	const [activeDetailsTab, setActiveDetailsTab] = useState('details');
	const [localTariffs, setLocalTariffs] = useState<TariffListNode[]>([]);
	const [itemsToFetch, setItemsToFetch] = useState(DEFAULT_PAGE_SIZE);

	const {
		data,
		loading: isLoading,
		refetch,
		fetchMore
	} = useQuery<TariffsListResponse>(TARIFFS_LIST_QUERY, {
		variables: { first: itemsToFetch }
	});

	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const hasNextPage = data?.tariffs?.pageInfo?.hasNextPage ?? false;
	const endCursor = data?.tariffs?.pageInfo?.endCursor;

	const handleLoadMore = useCallback(async () => {
		if (!hasNextPage || isLoadingMore || !endCursor) return;

		setIsLoadingMore(true);
		try {
			await fetchMore({
				variables: { first: DEFAULT_PAGE_SIZE, after: endCursor },
				updateQuery: (prev, { fetchMoreResult }) => {
					if (!fetchMoreResult?.tariffs) return prev;
					return {
						...prev,
						tariffs: {
							...fetchMoreResult.tariffs,
							nodes: [
								...(prev.tariffs?.nodes ?? []),
								...(fetchMoreResult.tariffs.nodes ?? [])
							]
						}
					};
				}
			});
			setLocalTariffs([]);
		} catch (error) {
			console.error('Error loading more tariffs:', error);
		} finally {
			setIsLoadingMore(false);
		}
	}, [fetchMore, hasNextPage, isLoadingMore, endCursor]);

	const tariffsData = data?.tariffs?.nodes ?? [];

	const handleRefresh = () => {
		setLocalTariffs([]);
		setItemsToFetch(DEFAULT_PAGE_SIZE);
		refetch({ first: DEFAULT_PAGE_SIZE });
	};

	const allTariffs = useMemo(() => {
		const serverTariffs = tariffsData || [];
		if (localTariffs.length === 0 || serverTariffs.length === 0) {
			return serverTariffs;
		}

		const mergedTariffs = [...serverTariffs];
		for (const localTariff of localTariffs) {
			const existingIndex = mergedTariffs.findIndex(
				t => t.tariffId === localTariff.tariffId
			);
			if (existingIndex >= 0) {
				mergedTariffs[existingIndex] = localTariff;
			} else {
				mergedTariffs.unshift(localTariff);
			}
		}
		return mergedTariffs;
	}, [tariffsData, localTariffs]);

	// Handle tariff selection from location state (after create/edit)
	useEffect(() => {
		const state = location.state as { selectTariffId?: string } | null;
		if (state?.selectTariffId && allTariffs.length > 0 && !isLoading) {
			const tariffIdToSelect = state.selectTariffId;
			// Trigger the selection after a short delay to ensure grid is ready
			const timeoutId = setTimeout(async () => {
				try {
					await selectTariffAfterSave({
						tariffId: tariffIdToSelect,
						client: apolloClient,
						currentTariffs: allTariffs,
						onDataUpdate: setLocalTariffs,
						onTariffSelect: setSelectedTariff
					});
					// Clear the location state after processing to prevent re-triggering
					navigate(location.pathname, { replace: true, state: null });
				} catch (error) {
					console.error(
						'Failed to select tariff after navigation:',
						error
					);
				}
			}, 100);

			return () => clearTimeout(timeoutId);
		}
	}, [
		location.state,
		allTariffs.length,
		isLoading,
		apolloClient,
		allTariffs,
		navigate,
		location.pathname
	]);

	const handleRowClick = (tariff: TariffListNode) => {
		setSelectedTariff(tariff);
	};

	const handleSetMainTariff = useCallback(async (tariffId: string) => {
		const confirmed = await showDialog(
			t('setMain.confirmTitle'),
			t('setMain.confirmDescription'),
			{
				confirmLabel: t('setMain.confirmButton'),
				confirmVariant: 'default'
			}
		);

		if (!confirmed) return;

		try {
			await setMainTariff(
				getMutationOptions('tariffs', { input: { tariffId } }, {
					additionalRefetch: ['Tariff']
				})
			);
			showMutationSuccessToast('tariffs', 'update');
		} catch (error) {
			console.error('Failed to set main tariff:', error);
		}
	}, [showDialog, setMainTariff, t]);

	const columns: ColDef<TariffListNode>[] = [
		{
			headerName: t('list.columns.name'),
			field: 'name',
			flex: 1,
			minWidth: 200
		},
		{
			headerName: t('list.columns.main'),
			field: 'mainInFacility',
			width: 100,
			minWidth: 80,
			cellRenderer: (params: any) => {
				const isMain = !!params.data?.mainInFacility;
				if (!isMain) return null;
				return (
					<Badge variant="default" className="bg-primary/15 text-primary border-primary/30 hover:bg-primary/15 font-medium text-xs">
						{t('list.columns.mainBadge')}
					</Badge>
				);
			}
		},
		{
			headerName: t('list.columns.version'),
			field: 'version',
			width: 120,
			minWidth: 100
		},
		{
			headerName: t('list.columns.status'),
			field: 'status',
			width: 120,
			minWidth: 100,
			cellRenderer: (params: any) => {
				const status = params.data?.status;
				return getTariffStatusLabel(status);
			}
		}
	];

	return (
		<ListPageLayout<TariffListNode>
			gridId="tariffs-list"
			columns={columns}
			data={allTariffs}
			title={t('list.title')}
			description={t('list.description')}
			searchPlaceholder={t('list.searchPlaceholder')}
			isLoading={isLoading}
			onRowClick={handleRowClick}
			onCreate={goToCreate}
			onRefresh={handleRefresh}
			selectedItem={selectedTariff}
			detailsTitle="Tariff Details"
			onEdit={goToEdit}
			onOpenInNewWindow={id => openEntityInNewWindow('tariffs', id)}
			renderDetailsContent={tariff => (
				<TariffDetails
					tariffId={tariff.tariffId}
					activeTab={activeDetailsTab}
					onTabChange={setActiveDetailsTab}
				/>
			)}
			renderDetailsHeader={(tariff, actions) => (
				<TariffDetailsHeader
					tariff={tariff}
					isMain={!!tariff.mainInFacility}
					onSetMain={() => handleSetMainTariff(tariff.tariffId)}
					{...actions}
				/>
			)}
			getRecordId={tariff => tariff.tariffId}
			showCreateButton
			showRefreshButton
			hasNextPage={hasNextPage}
			isLoadingMore={isLoadingMore}
			onLoadMore={handleLoadMore}
			entityName={t('entityName')}
		/>
	);
};
